import { useCallback, useEffect, useMemo, useState } from 'react';
import { JustRightHeightModal } from 'components/styled/scoped';
import { Form, Input, message, Select } from 'antd';
import { useTranslate } from '@tolgee/react';
import QuestionExample from '@knowledge/knowledges/spu/createOrEdit/formItem/QuestionExample';
import { useRequest } from 'ahooks';
import { getSpuClass } from '@knowledge/service/api/single';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { getMultiLangValue } from './helpers';
import { useAtomValue } from 'jotai';
import { currentClassId, currentKcList } from '@knowledge/knowledges/singleClass/state';
import { refreshKcList } from '@knowledge/knowledges/singleClass/connect';
import { createKnowledge } from '@knowledge/service/api/spu';

type NewKcProps = {
  visible: boolean;
  onCancel: () => void;
};

const safeParse = (str: string) => {
  try {
    return JSON.parse(str);
  } catch (e) {
    return {};
  }
};

export const useVerifyRepeat = (id?: number) => {
  const list = useAtomValue(currentKcList);
  return useCallback(
    (value: string) => {
      const excludeSelf = id ? list?.filter((item) => item.id !== id) : list;
      return excludeSelf.some((item) => {
        const multiLang = safeParse(item.nameMultiLang);
        console.log(Object.values(multiLang), value);
        return Object.values(multiLang).includes(value) || item.knowledgeName === value;
      });
    },
    [id, list],
  );
};

const NewKc = (props: NewKcProps) => {
  const { visible, onCancel } = props;
  const { t } = useTranslate();
  const [form] = Form.useForm();
  const spuClassId = useAtomValue(currentClassId);
  const { data } = useRequest<Knowledge.SingleClass.Value[], any>(() => getSpuClass(), {
    onError: () => {
      message.error(t('phrase.获取数据失败，请稍后再试'));
    },
  });
  const { runAsync: create, loading } = useRequest(createKnowledge, {
    manual: true,
  });
  const lang = useSystemLanguageValue();
  const options = useMemo(() => {
    return data?.map(({ id, name, nameMultiLang }) => ({
      id,
      name: getMultiLangValue(nameMultiLang, lang, name),
    }));
  }, [data, lang]);
  useEffect(() => {
    form.setFieldsValue({
      spuClassId: Number(spuClassId),
    });
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spuClassId]);

  const handleOk = useCallback(() => {
    form.validateFields().then((values) => {
      const formData = { ...values, values: [] };
      create({ spuKnowledge: formData })
        .then(() => {
          onCancel();
          refreshKcList();
          message.success(t('operation.saveSucceed'));
        })
        .catch(() => {
          message.error(t('operation.saveFailed'));
        });
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const verifyRepeat = useVerifyRepeat();

  return (
    <JustRightHeightModal
      visible={visible}
      onCancel={onCancel}
      title={t('new_knowledge')}
      width={390}
      onOk={handleOk}
      centered
      okButtonProps={{
        loading,
      }}
    >
      <Form form={form} layout="vertical" validateTrigger={['onBlur']}>
        <Form.Item label={t('single_choose_self')} required name="spuClassId">
          <Select>
            {options?.map(({ id, name }) => (
              <Select.Option key={String(id)} value={id}>
                {name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          label={t('knowledge.knowledgeName')}
          required
          name="knowledgeName"
          rules={[
            {
              required: true,
              validateTrigger: 'onBlur',
              validator: (_, value) => {
                if (!value) {
                  return Promise.reject(
                    t('department.请输入x', { x: t('knowledge.knowledgeName') }),
                  );
                }
                if (verifyRepeat(value)) {
                  return Promise.reject(t('single_kc_repeat'));
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <Input placeholder={t('Entities.请输入')} maxLength={60} showCount />
        </Form.Item>
        <Form.Item label={t('knowledge.examples')} name="querySamples">
          <QuestionExample />
        </Form.Item>
      </Form>
    </JustRightHeightModal>
  );
};

NewKc.displayName = 'NewKc';

const useNewKc = () => {
  const [visible, setVisible] = useState(false);
  const open = useCallback(() => {
    setVisible(true);
  }, []);
  const close = useCallback(() => {
    setVisible(false);
  }, []);

  const instance = visible && <NewKc visible={visible} onCancel={close} />;

  return [open, instance] as const;
};

export default useNewKc;
